import { create } from 'zustand';

import type { PlaybooksGenerationInputs } from './schemas';
import { closeUsageSidebar } from './usage-sidebar-store';

/**
 * Launcher V2 U6 — the "Generate with AI" drawer's session state (U7 builds
 * `GenerateWithAIDrawer` against this store; this bead only creates it and
 * wires the two OQ4 cross-store closes below).
 *
 * A "session" is the drawer being open at all, walking `configure` →
 * `review` → `confirm` → `done` for ONE (editing scope, sequence) pair. It is
 * deliberately separate from `generated-copy-store.ts`'s per-step review
 * data: the session is the DRAWER's UI state (which stage, the rep's
 * in-progress form/names, the confirm result), while the review data is the
 * STAGED GENERATION itself — steps, edits, accept/reject decisions — which
 * must survive the drawer closing (R13) so a rep who dismisses the drawer
 * mid-review and reopens it resumes exactly where they left off.
 *
 * ── OQ4 — MUTUALLY EXCLUSIVE WITH THE USAGE SIDEBAR ────────────────────────
 * (Bead's User Decisions.) A rep cannot be mid-save-gate
 * (`usage-sidebar-store.ts`'s `PendingUsageSidebar`) and mid-generate at the
 * same time — both are full-attention modal-ish flows over the same editor
 * surface. `openGenerateSession` therefore calls {@link closeUsageSidebar}
 * unconditionally before opening; symmetrically, `usage-sidebar-store.ts`'s
 * `openUsageSidebar` calls {@link closeGenerateSession} before it opens.
 * Each close touches ONLY its own store's session state — neither one drops
 * the OTHER store's staged review data (`generated-copy-store.ts`'s entries,
 * or a hand-typed copy draft), which is the whole point of R13's
 * route-survival guarantee: "openGenerateSession() closes the usage sidebar;
 * openUsageSidebar() closes only the AI drawer's visibility and the staged
 * generation survives."
 *
 * This is a deliberate, small circular import between this file and
 * `usage-sidebar-store.ts` — both references are only ever called from
 * inside an action function, never at module-eval time, so it resolves fine
 * under ESM/bundler module loading. It exists here, in the stores, rather
 * than being left to every call site to remember, because OQ4 is a store-
 * level invariant (any caller opening one surface must close the other), not
 * a per-component convenience.
 *
 * Module-scope zustand (root CLAUDE.md "Zustand for Module-Scope Client
 * State"), same conventions as every other store in this app: plain
 * `create<State>(…)`, exported {@link initialState} so tests reset with
 * `useGenerateWithAIStore.setState(initialState, true)`, and actions as
 * module-level functions rather than fields on the state object.
 */

/** The drawer's stage-1 configure form. `campaignSfdcId: null` until a campaign is chosen (library surface) or seeded (embedded surface). */
export interface GenerateWithAIForm {
  campaignSfdcId: string | null;
  eventName: string;
  campaignDescription: string;
  memberStatuses: string[];
  instruction: string;
  sourceMaterial: string;
}

/**
 * terra-qyt.10 — the drawer's ending is now "Apply to sequence" (the host's
 * gated save writes the approved steps onto the CURRENT sequence in place),
 * not a third-identity create. `'confirm'`/`'done'` and the stage-3 rep-typed
 * `names`/`result` this session used to carry for that flow are gone — see
 * this file's own module doc and `GenerateWithAIDrawer.tsx`'s `handleApply`.
 */
export type GenerateWithAIStage = 'configure' | 'review';

export interface GenerateWithAISession {
  /** The editing SURFACE this session belongs to — a rung's `draftKey`, or `'library'`. Same vocabulary `generated-copy-store.ts`'s `scope` uses. */
  scope: string;
  sequenceId: string;
  surface: 'embedded' | 'library';
  stage: GenerateWithAIStage;
  form: GenerateWithAIForm;
}

interface GenerateWithAIState {
  /** `null` when the drawer is closed. At most one session exists at a time — opening a new one replaces it. */
  session: GenerateWithAISession | null;
}

/** Exported per the store convention — reset idiom is `useGenerateWithAIStore.setState(initialState, true)`. */
export const initialState: GenerateWithAIState = {
  session: null,
};

export const useGenerateWithAIStore = create<GenerateWithAIState>(() => ({ ...initialState }));

const EMPTY_FORM: GenerateWithAIForm = {
  campaignSfdcId: null,
  eventName: '',
  campaignDescription: '',
  memberStatuses: [],
  instruction: '',
  sourceMaterial: '',
};

/**
 * Opens a brand-new session at stage `configure`, replacing any existing one
 * unconditionally (the same "rapid reclick" posture `usage-sidebar-store.ts`'s
 * `openUsageSidebar` takes). `seed` overlays only the fields it provides onto
 * {@link EMPTY_FORM} — everything else starts blank for the drawer's own
 * `generation-context` fetch (U4b's `getGenerationContext`) to seed once it
 * resolves.
 *
 * `restoreFrom` (D6) — the `PlaybooksGenerationInputs` snapshot read off
 * `generated-copy-store.ts`'s `generationSnapshot` selector when a staged
 * generation ALREADY exists for this (scope, sequenceId). When present it
 * WINS over `seed`: reopening the drawer on a sequence with an in-progress
 * or completed generation must show the inputs that ACTUALLY produced that
 * staged copy, not whatever the caller's own reseed logic would otherwise
 * guess — `handleConfirm` (Wave C) later reads the same snapshot, never the
 * live form, so the two must start in agreement.
 *
 * Closes an open usage sidebar first — see the module doc's "OQ4". Does NOT
 * touch `generated-copy-store.ts` — a staged generation there (from a PRIOR
 * session on the same scope+sequence) survives, which is how reopening the
 * drawer for the same sequence resumes at `review` instead of starting over.
 */
export function openGenerateSession(input: {
  scope: string;
  sequenceId: string;
  surface: 'embedded' | 'library';
  seed?: Partial<GenerateWithAIForm>;
  restoreFrom?: PlaybooksGenerationInputs;
}): void {
  closeUsageSidebar();
  const form = input.restoreFrom
    ? {
        campaignSfdcId: input.restoreFrom.campaignSfdcId,
        eventName: input.restoreFrom.eventName,
        campaignDescription: input.restoreFrom.campaignDescription,
        memberStatuses: input.restoreFrom.memberStatuses,
        instruction: input.restoreFrom.instruction,
        sourceMaterial: input.restoreFrom.sourceMaterial,
      }
    : { ...EMPTY_FORM, ...input.seed };
  useGenerateWithAIStore.setState({
    session: {
      scope: input.scope,
      sequenceId: input.sequenceId,
      surface: input.surface,
      stage: 'configure',
      form,
    },
  });
}

/** Merges a patch onto the open session's form. A no-op when no session is open. */
export function updateGenerateForm(patch: Partial<GenerateWithAIForm>): void {
  useGenerateWithAIStore.setState((state) =>
    state.session === null ? state : { session: { ...state.session, form: { ...state.session.form, ...patch } } },
  );
}

/** Advances (or rewinds — "Back to review") the open session's stage. A no-op when no session is open. */
export function setGenerateStage(stage: GenerateWithAIStage): void {
  useGenerateWithAIStore.setState((state) =>
    state.session === null ? state : { session: { ...state.session, stage } },
  );
}

/**
 * Closes the drawer — resets to {@link initialState}. Deliberately does NOT
 * touch `generated-copy-store.ts`: U7's header-X closes ONLY this session,
 * and the staged review survives so reopening resumes at `review` (R13).
 */
export function closeGenerateSession(): void {
  useGenerateWithAIStore.setState(initialState, true);
}

export function selectGenerateSession(state: GenerateWithAIState): GenerateWithAISession | null {
  return state.session;
}

/** True only when a session is open for the EXACT (scope, sequenceId) pair — used to gate a rung's own drawer mount. */
export function selectIsSessionOpenFor(
  scope: string,
  sequenceId: string,
): (state: GenerateWithAIState) => boolean {
  return (state) => state.session?.scope === scope && state.session?.sequenceId === sequenceId;
}
